/**
 * Sudoku rendering and layout constants. All values are canvas pixels (the
 * canvas is the whole game surface — no DOM chrome to size in rem). The grid
 * is fixed at 9×9 with 3×3 boxes regardless of variant; future 6×6 / 4×4 will
 * introduce their own constants behind a variant seam.
 */

// ── Grid topology ──────────────────────────────────────────────────────────
export const BOARD_ROWS = 9;
export const BOARD_COLS = 9;
export const BOARD_CELLS = BOARD_ROWS * BOARD_COLS;
export const BOX_SIZE = 3;
export const BOXES_PER_AXIS = BOARD_ROWS / BOX_SIZE;

// ── Breakpoints ────────────────────────────────────────────────────────────
// Re-exported from layout-core so the rest of the app has a single import.
// The same MOBILE_BREAKPOINT_PX (768) drives the "stack numpad below board"
// vs "board centered, numpad below" choice — desktop and mobile end up using
// the same vertical stack but with different padding + cell-size caps.
export {
  MOBILE_BREAKPOINT_PX,
  TABLET_BREAKPOINT_PX,
} from '@bn-games/layout-core';

// ── Board padding / margins ────────────────────────────────────────────────
/** Outer canvas padding on mobile — zero so the frame fills the viewport edge
 *  to edge (matches backgammon's MOBILE_CANVAS_MARGIN_PX). */
export const BOARD_PADDING_MOBILE_PX = 0;
/** Outer canvas padding on desktop — gives the frame breathing room. */
export const BOARD_PADDING_DESKTOP_PX = 32;
/** Thickness of the seam between the playing surface and the controls tray
 *  inside the shared wood frame. Mirrors backgammon's central groove. */
export const BOARD_CONTROLS_SEAM_PX = 12;
/** Inner padding between the controls tray's inner edge and the buttons. */
export const CONTROLS_INNER_PADDING_PX = 12;

// ── Cell sizing ────────────────────────────────────────────────────────────
/**
 * Cap on individual cell size so the board doesn't balloon on 4K monitors.
 * 64px × 9 = 576px board, comfortable on a 1440p workspace.
 */
export const MAX_CELL_SIZE_PX = 64;
/**
 * Floor on cell size — below this the board stops being legible. Layout
 * compresses other things (numpad button size) before shrinking below this.
 */
export const MIN_CELL_SIZE_PX = 28;

// ── Grid line thickness ────────────────────────────────────────────────────
/** Hairline between cells inside a 3×3 box. */
export const GRID_LINE_THIN_PX = 1;
/** Heavy line at 3×3 box borders. Reads cleanly at typical cell sizes. */
export const GRID_LINE_THICK_PX = 3;

// ── Numpad ─────────────────────────────────────────────────────────────────
/** Number of digit buttons (1–9). */
export const NUMPAD_DIGIT_COUNT = 9;
/** Total button count = 4 actions (undo / erase / pencil / hint) + 9 digits. */
export const NUMPAD_BUTTON_COUNT = NUMPAD_DIGIT_COUNT + 4;
/** Digit row is a single 9-column strip (1-9 side by side). */
export const NUMPAD_DIGIT_COLUMNS = 9;
/** Action row above the digit strip: undo / erase / pencil / hint. */
export const NUMPAD_ACTION_COLUMNS = 4;
/** Minimum touch-target edge (WCAG / mobile-OS guideline). */
export const NUMPAD_MIN_BUTTON_PX = 44;
/** Cap on digit-button size — prevents giant buttons on huge monitors. */
export const NUMPAD_MAX_BUTTON_PX = 64;
/** Gap between adjacent numpad buttons. */
export const NUMPAD_BUTTON_GAP_PX = 6;

// ── Numpad rendering ───────────────────────────────────────────────────────
/** Corner radius applied to every numpad button, as a fraction of buttonSize. */
export const NUMPAD_BUTTON_RADIUS_FRACTION = 0.18;
/** Digit-label font size as a fraction of digit button height. Bigger than the
 *  pre-redesign 0.5 to match the "big bold blue" feel of the screenshot. */
export const NUMPAD_DIGIT_FONT_SCALE = 0.62;
/** Icon-glyph font size for action-row buttons, as a fraction of action-row
 *  icon area (= buttonSize). */
export const NUMPAD_ICON_FONT_SCALE = 0.46;
/** A digit is considered exhausted (disabled) when this many copies are placed. */
export const NUMPAD_DIGIT_EXHAUSTED_COUNT = 9;
/** Small caption that sits under each action-row icon ("Undo", "Erase", …). */
export const NUMPAD_ACTION_LABEL_FONT_PX = 11;
/** Gap between the action icon and the caption below it. */
export const NUMPAD_ACTION_LABEL_GAP_PX = 3;
/** Badge (hint count / pencil ON/OFF) painted at the top-right of an action. */
export const NUMPAD_ACTION_BADGE_HEIGHT_PX = 16;
export const NUMPAD_ACTION_BADGE_FONT_PX = 10;
export const NUMPAD_ACTION_BADGE_HORIZONTAL_PADDING_PX = 5;
export const NUMPAD_ACTION_BADGE_RADIUS_PX = 8;
export const NUMPAD_ACTION_BADGE_INSET_PX = 4;

// ── Status bar (top stats strip — also the mobile pause entry) ────────────
/** Vertical room reserved for the top stats strip (game / errors / time + pause).
 *  The strip is a recessed wood-tray well above the board — mirrors the
 *  numpad tray below the board so the frame reads as one stack. */
export const STATUS_BAR_HEIGHT_PX = 56;
/** Inner horizontal padding inside the status bar (between content + edges). */
export const STATUS_BAR_HORIZONTAL_PADDING_PX = 12;
export const STATUS_BAR_VALUE_FONT_PX = 18;
export const STATUS_BAR_LABEL_FONT_PX = 11;
export const STATUS_BAR_COLUMN_GAP_PX = 6;
/** Hit-target for the pause button on the right side of the status bar. */
export const PAUSE_BUTTON_SIZE_PX = 40;
/** Visual size of the pause-icon bars inside the pause button. */
export const PAUSE_GLYPH_BAR_WIDTH_PX = 4;
export const PAUSE_GLYPH_BAR_HEIGHT_PX = 16;
export const PAUSE_GLYPH_GAP_PX = 5;

// ── Scene runtime ──────────────────────────────────────────────────────────
/** Error-flash duration after a wrong digit is entered. */
export const ERROR_FLASH_MS = 500;
/** Hint highlight duration after `requestHint` returns a Hint. */
export const HINT_HIGHLIGHT_MS = 4000;
/** Win banner duration before the scene auto-exits back to the menu. */
export const WIN_BANNER_MS = 1800;
/** Cell index used as the initial selection target on first arrow press
 *  when no cell is yet selected. row=4, col=4 → 4 * 9 + 4 = 40. */
export const CENTER_CELL_INDEX = 40;
/** Stopwatch ceiling — clamps display to `99:59` so the label can't outgrow
 *  the timer slot during very long sessions. */
export const STOPWATCH_MAX_MS = (99 * 60 + 59) * 1000;
/** Win banner font size (canvas px). */
export const WIN_BANNER_FONT_PX = 36;
/** Wood frame thickness on desktop. */
export const BOARD_FRAME_THICKNESS_PX = 28;
/** Wood frame thickness on mobile — thinner than desktop, but still drawn.
 *  Because mobile padding is 0, this thickness sits flush against the viewport
 *  edges and the frame reads as full-screen. */
export const BOARD_FRAME_THICKNESS_MOBILE_PX = 16;
/** Frame bevel highlight/shadow thickness (canvas px). */
export const BOARD_FRAME_BEVEL_PX = 2;
